import { useInfiniteQuery } from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import { getNextPageParamFromPaginatedResult } from '@/api/infiniteQueries';
import pipelineKeys from '@/api/queries/pipelines/keyFactory';
import { graphql } from '@/gql';
import { GetPipelineStageTargetingAutomationsQuery } from '@/gql/graphql';
import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';

/**
 * Fragment for fetching automation data needed for TargetingAutomationCard.
 *
 * ## Conditions
 * Uses interface spreading to efficiently fetch shared fields across the union:
 * - `ICondition`: All condition types implement this, giving us `operator`
 * - `IStringMatchCondition`: Many conditions implement this, giving us `value`
 *
 * Conditions with unique fields (e.g. `invoiceStatus`, `pipeline`) need
 * type-specific fragments.
 *
 * ## Actions
 * Each action type may have unique fields. Some actions only need `__typename`
 * for a static translation label. Field aliases (e.g. `taskTitle: title`) are
 * used when multiple action types have the same field name but different return types.
 *
 * @see automationDescriptions.ts for the translation logic that consumes this data
 */
graphql(`
  fragment PipelineTargetingAutomationCard on Automation {
    id
    privateId
    title
    isActive
    triggerEvent
    entityType
    conditions {
      __typename
      ... on ICondition {
        operator
      }
      ... on IStringMatchCondition {
        value
      }
      ... on ConnectionApplicationStatusCondition {
        connectionApplicationStatus
      }
      ... on EventTaskStatusCondition {
        eventTaskStatus
      }
      ... on InvoiceIsDepositCondition {
        isDepositInvoice
      }
      ... on InvoiceStatusCondition {
        invoiceStatus
      }
      ... on ProposalStatusCondition {
        proposalStatus
      }
      ... on TriggeredDayOfWeekCondition {
        triggeredDayOfWeek
      }
      ... on ProjectPipelineCondition {
        pipeline {
          title
        }
      }
    }
    actions {
      __typename
      ... on CreateProjectTaskAutomationAction {
        taskTitle: title
      }
      ... on EmailNotificationAutomationAction {
        sendToEmail
      }
      ... on MoveProjectToPipelineStageAutomationAction {
        targetStage {
          title
        }
      }
      ... on SMSNotificationAutomationAction {
        sendToNumber
      }
      ... on TagEventAutomationAction {
        tags {
          name
        }
      }
      ... on TagProjectAutomationAction {
        tags {
          name
        }
      }
      ... on RemoveEventTagsAutomationAction {
        tags {
          name
        }
      }
      ... on RemoveProjectTagsAutomationAction {
        tags {
          name
        }
      }
    }
  }
`);

const GET_PIPELINE_STAGE_TARGETING_AUTOMATIONS = graphql(`
  query GetPipelineStageTargetingAutomations(
    $id: ID!
    $pagination: PaginationInput!
  ) {
    pipelineStage(id: $id) {
      id
      publicId
      targetingAutomations(pagination: $pagination) {
        items {
          ...PipelineTargetingAutomationCard
        }
        totalCount
        offset
        limit
      }
    }
  }
`);

export type GetPipelineStageTargetingAutomationsResponse =
  GetPipelineStageTargetingAutomationsQuery['pipelineStage'];
type GetPipelineStageTargetingAutomationResponse = NonNullable<
  GetPipelineStageTargetingAutomationsQuery['pipelineStage']
>['targetingAutomations']['items'][0];

type UseGetPipelineStageInfiniteTargetingAutomationsOptions = {
  pipelinePublicId: string;
  pipelineStagePublicId: string;
};

const useGetPipelineStageInfiniteTargetingAutomations = (
  args: UseGetPipelineStageInfiniteTargetingAutomationsOptions
) => {
  const { pipelinePublicId, pipelineStagePublicId } = args;

  const queryFn = ({ pageParam }: { pageParam: number }) => {
    return gqlClient.request(GET_PIPELINE_STAGE_TARGETING_AUTOMATIONS, {
      id: pipelineStagePublicId,
      pagination: {
        offset: pageParam,
        limit: PAGINATION_ARG_DEFAULTS.limit,
      },
    });
  };

  const { data, ...rest } = useInfiniteQuery({
    queryKey: pipelineKeys.pipelineStageTargetingAutomations(
      pipelinePublicId,
      pipelineStagePublicId
    ),
    initialPageParam: PAGINATION_ARG_DEFAULTS.offset,
    queryFn,
    getNextPageParam: getNextPageParamFromPaginatedResult(
      (lastPage) => lastPage.pipelineStage?.targetingAutomations
    ),
  });

  return {
    data,
    targetingAutomations: data?.pages.flatMap(
      (page) => page.pipelineStage?.targetingAutomations.items ?? []
    ),
    totalCount: data?.pages[0]?.pipelineStage?.targetingAutomations.totalCount,
    ...rest,
  };
};

export default useGetPipelineStageInfiniteTargetingAutomations;
